import React, { useEffect, useRef, useState } from 'react';
import type { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { Modal } from '../Modal';
import { ModalFooter } from '../ModalFooter';
import { MsComboBox } from '../MsComboBox';
import { useProject, useUpdateProject } from '../../hooks/useProject';
import { getConfigService } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

const USAGE_TYPES = [
  { id: 'processing', name: 'Processing' },
  { id: 'eca', name: 'ECA' },
  { id: 'review', name: 'Review' },
];

interface Props {
  projectId: string;
  onClose: () => void;
}

export function CreditCardModal({ projectId, onClose }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const cardElRef = useRef<StripeCardElement | null>(null);

  const [cardReady, setCardReady] = useState(false);
  const [addressReady, setAddressReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  const { data: project } = useProject(projectId);
  const [usageType, setUsageType] = useState<typeof USAGE_TYPES>([]);
  const updateProject = useUpdateProject();

  // Pre-select usage type once project data loads
  useEffect(() => {
    if (project?.usageType && usageType.length === 0) {
      const match = USAGE_TYPES.find(u => u.id === project.usageType);
      if (match) setUsageType([match]);
    }
  }, [project?.usageType]);

  useEffect(() => {
    let cardEl: StripeCardElement | null = null;
    (async () => {
      const { loadStripe } = await import('@stripe/stripe-js');
      const pubKey = getConfigService().getStripePubKey();
      const stripe = await loadStripe(pubKey);
      if (!stripe || !cardRef.current || !addressRef.current) return;
      stripeRef.current = stripe;

      const appearance: any = {
        labels: 'floating',
        variables: {
          colorPrimary: '#0570de',
          colorBackground: '#ffffff',
          colorText: '#555555',
          colorTextPlaceholder: '#555555',
          gridRowSpacing: '4px',
          borderRadius: '4px',
          fontSizeBase: '14px',
        },
      };
      const elements = stripe.elements({ mode: 'setup', currency: 'usd', appearance });
      const addressEl = elements.create('address', { mode: 'billing' });
      cardEl = elements.create('card', {
        disableLink: true,
        hidePostalCode: true,
        classes: { base: 'stripe-card', focus: 'stripe-card--focused' },
        style: { base: { color: '#555555', fontSize: '14px', '::placeholder': { color: '#555555' } } },
      });
      cardElRef.current = cardEl;

      cardEl.mount(cardRef.current);
      addressEl.mount(addressRef.current);

      cardEl.on('change', (e: any) => setCardReady(e.complete));
      addressEl.on('change', (e: any) => setAddressReady(e.complete));

      (stripeRef as any).addressEl = addressEl;
    })();

    return () => { cardEl?.unmount(); };
  }, []);

  const handleSubmit = async () => {
    const stripe = stripeRef.current;
    const cardEl = cardElRef.current;
    const addressEl = (stripeRef as any).addressEl;
    if (!stripe || !cardEl || !addressEl) return;

    setSaving(true);
    setError(null);

    const address = await addressEl.getValue();
    if (!address.complete) { setSaving(false); return; }

    stripe.createToken(cardEl, {
      name: address.value.name,
      address_line1: address.value.address.line1,
      address_line2: address.value.address.line2 ?? '',
      address_city: address.value.address.city,
      address_state: address.value.address.state,
      address_zip: address.value.address.postal_code,
      address_country: address.value.address.country,
    }).then(result => {
      if (result.error) {
        setError(result.error.message ?? 'Card error');
        setSaving(false);
        return;
      }
      updateProject.mutate(
        { ...project, id: projectId, token: result.token?.id, usageType: usageType[0]?.id } as any,
        {
          onSuccess: () => { notifySuccess({ title: 'Credit Card Updated', description: 'Thank you for being awesome.', timeout: 5000 }); onClose(); },
          onError: () => { setSaving(false); },
        }
      );
    });
  };

  const isTrial = project?.status === 'trial';
  const canSubmit = cardReady && addressReady && accepted && usageType.length > 0;

  return (
    <Modal
      title="Update Credit Card"
      onClose={onClose}
      testId="credit-card-modal"
      footer={
        <ModalFooter
          onClose={onClose}
          onNext={handleSubmit}
          nextLabel="Save"
          nextDisabled={!canSubmit}
          saving={saving}
          savingLabel="Saving…"
        />
      }
    >
      {project && (
        <div className="form-group">
          <h4>Project: <small><i className="fas fa-gavel" /> {project.name}</small></h4>
        </div>
      )}

      <div className={`form-group${usageType.length === 0 ? ' has-error' : ''}`}>
        {usageType.length === 0 && <span className="help-block">Usage Type is required</span>}
        <MsComboBox
          id="usage-type"
          testId="credit-card-usage-type-select"
          selected={usageType}
          onChange={items => setUsageType(items.slice(-1))}
          items={USAGE_TYPES}
          getKey={u => u.id}
          getLabel={u => u.name}
          placeholder="Project Usage Type"
        />
      </div>

      <div className="form-group">
        <i className="fab fa-cc-visa fa-2x" style={{ color: '#cccccc' }} />
        {' '}
        <i className="fab fa-cc-mastercard fa-2x" style={{ color: '#cccccc' }} />
        {' '}
        <i className="fab fa-cc-amex fa-2x" style={{ color: '#cccccc' }} />
        {' '}
        <i className="fab fa-cc-discover fa-2x" style={{ color: '#cccccc' }} />
        {' '}
        <i className="fab fa-cc-jcb fa-2x" style={{ color: '#cccccc' }} />

        <br /><br />
        <div ref={cardRef} id="card-element" data-testid="credit-card-card-panel" />
        <div ref={addressRef} id="address-element" data-testid="credit-card-address-panel" />
      </div>

      {isTrial && (
        <div className="alert alert-warning" role="alert" style={{ marginTop: 25 }}>
          By entering a credit card this project will become <b className="label label-success">Active</b>. Please check
          the box below to verify that you understand this project will now have billable events.
        </div>
      )}

      <div className="checkbox checkbox-primary">
        <input
          id="acceptTermsAndConditions"
          data-testid="credit-card-accept-terms-checkbox"
          type="checkbox"
          checked={accepted}
          onChange={e => setAccepted(e.target.checked)}
        />
        <label htmlFor="acceptTermsAndConditions">
          I want this project to be <b className="label label-success">Active</b> status and accept all billable charges associated.
        </label>
      </div>

      {error && <p className="text-danger">{error}</p>}
    </Modal>
  );
}
